// Phase 2 store — API-backed (RDS via the digiroadmapapi NestJS middleware).
//
// DROP-IN REPLACEMENT for src/lib/store.ts in the CRA `digitalroadmap` repo:
// rename this to store.ts there. It keeps the EXACT hook signatures of the
// localStorage store, so no component changes:
//   useRoster()      -> { roster, hydrated, addPerson(name, email?): Person }
//   useInitiatives() -> { initiatives, hydrated, create(data): Initiative,
//                          update(id, patch), archive(id), restore(id) }
//
// Strategy: optimistic updates with client-generated ids. Mutations return the
// object synchronously (so callers that read `.id`/`.name` right away keep
// working), fire the request in the background, and roll back + toast on error.
//
// NOTE (CRA): no "use client" directive — that's a Next.js concept. Env var is
// CRA-style: set REACT_APP_API_BASE to override the default "/api" (same-origin
// via the nginx /api/* proxy).

import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import type { Initiative, Person, Status, Update } from "./types";
import { makeInitials, colorForIndex } from "./constants";
import { nextSortOrder } from "./ordering";
import { lifecycleAtCreate, withLifecycleStamps } from "./baseline";
import { API_BASE, API_KEY } from "./config";
import { getAccessToken, isSignedIn } from "./auth";
import { resolvePersonReuse, normalizeEmail, isDifferentPerson } from "./people";

// ---------- id generation ----------

function uuid(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  // Fallback for older runtimes.
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
}

// ---------- wire types + date mapping ----------
//
// The API speaks ISO date strings ("YYYY-MM-DD"). The frontend uses a
// [year, monthIndex(0-11), day] tuple internally. We convert at this boundary
// so the API stays idiomatic and components never see the tuple quirk change.

type DateTuple = [number, number, number];

// The API's end_date is dormant — the frontend no longer reads or writes it
// (the bar's right edge derives as shipped ?? target).
type ApiInitiative = Omit<Initiative, "target" | "start" | "shipped" | "originalTarget" | "tldr" | "dependsOnId" | "workstream"> & {
  tldr?: string | null; // nullable on the wire; normalized to "" at this boundary
  dependsOnId?: string | null; // null on the wire; undefined in the app
  workstream?: string | null;
  target: string | null;
  start?: string | null;
  shipped?: string | null;
  originalTarget?: string | null;
};

function tupleToISO(t: DateTuple): string {
  const [y, m, d] = t;
  const mm = String(m + 1).padStart(2, "0"); // monthIndex 0-11 -> 01-12
  const dd = String(d).padStart(2, "0");
  return `${y}-${mm}-${dd}`;
}

function isoToTuple(s: string): DateTuple {
  const [y, m, d] = s.split("-").map(Number);
  return [y, m - 1, d]; // 01-12 -> monthIndex 0-11
}

function fromApi(a: ApiInitiative): Initiative {
  return {
    ...a,
    tldr: a.tldr ?? "", // components can trust `tldr: string`
    sortOrder: a.sortOrder ?? 0,
    target: a.target ? isoToTuple(a.target) : undefined,
    start: a.start ? isoToTuple(a.start) : undefined,
    shipped: a.shipped ? isoToTuple(a.shipped) : undefined,
    originalTarget: a.originalTarget ? isoToTuple(a.originalTarget) : undefined,
    dependsOnId: a.dependsOnId ?? undefined,
    workstream: a.workstream ?? undefined,
  };
}

function toApi(i: Initiative): ApiInitiative {
  return {
    ...i,
    target: i.target ? tupleToISO(i.target) : null,
    start: i.start ? tupleToISO(i.start) : null,
    shipped: i.shipped ? tupleToISO(i.shipped) : null,
    originalTarget: i.originalTarget ? tupleToISO(i.originalTarget) : null,
  };
}

// Convert only the date-bearing keys of a partial patch to the wire shape.
function toApiPatch(patch: Partial<Initiative>): Record<string, unknown> {
  const out: Record<string, unknown> = { ...patch };
  if ("target" in patch) out.target = patch.target ? tupleToISO(patch.target) : null;
  if ("start" in patch) out.start = patch.start ? tupleToISO(patch.start) : null;
  if ("shipped" in patch) out.shipped = patch.shipped ? tupleToISO(patch.shipped) : null;
  if ("originalTarget" in patch) out.originalTarget = patch.originalTarget ? tupleToISO(patch.originalTarget) : null;
  // A cleared link arrives as `undefined`, which JSON.stringify drops — the
  // API would read "unchanged" and keep the old value. Send null to clear it.
  for (const k of ["notion", "jira", "design", "dependsOnId", "workstream"] as const) {
    if (k in patch) out[k] = patch[k] ?? null;
  }
  return out;
}

// ---------- fetch wrapper ----------

async function send(path: string, opts: RequestInit | undefined, forceRefresh: boolean): Promise<Response> {
  // Entra access token rides along when auth is enabled for this environment
  // (null otherwise — see auth.ts). The gateway validates it; the app never
  // parses or stores it beyond MSAL's own cache.
  const token = await getAccessToken({ forceRefresh });
  return fetch(`${API_BASE}${path}`, {
    headers: {
      "Content-Type": "application/json",
      // Gateway access key (per env) — required once API_BASE points at the
      // API Gateway; the backend's direct host ignores it. Not a per-user
      // secret — same value for every request in an environment.
      ...(API_KEY ? { "x-api-key": API_KEY } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...opts,
  });
}

async function api<T>(path: string, opts?: RequestInit): Promise<T> {
  const isRead = (opts?.method ?? "GET").toUpperCase() === "GET";
  let res: Response;
  try {
    res = await send(path, opts, false);
  } catch (err) {
    // The gateway's 401 carries no CORS headers, so a rejected token reaches
    // us as a bare "Failed to fetch". Renew the token and try once more.
    // Reads only: a write that failed this way may still have landed.
    if (!isRead || !isSignedIn()) throw err;
    res = await send(path, opts, true);
  }
  // A 401 we can see means the request was refused, so any method can retry.
  if (res.status === 401 && isSignedIn()) res = await send(path, opts, true);
  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`;
    try {
      const body = await res.json();
      if (body?.error) msg = body.error;
      else if (body?.message) msg = Array.isArray(body.message) ? body.message.join(", ") : body.message;
    } catch {
      /* non-JSON error body */
    }
    throw new Error(msg);
  }
  // 204 No Content -> nothing to parse.
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

// ---------- Hooks ----------

export function useRoster() {
  const [roster, setRoster] = useState<Person[]>([]);
  const [hydrated, setHydrated] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadKey, setLoadKey] = useState(0);
  const reload = useCallback(() => {
    setHydrated(false);
    setLoadError(null);
    setLoadKey(k => k + 1);
  }, []);
  // Keep a live snapshot so addPerson can read the current roster synchronously.
  const rosterRef = useRef<Person[]>([]);
  rosterRef.current = roster;

  useEffect(() => {
    let cancelled = false;
    api<Array<{ id: string; name: string; email?: string | null }>>("/people")
      .then((people) => {
        if (cancelled) return;
        // initials + color are derived here (the API stores id, name, email).
        setRoster(people.map((p, idx) => ({
          id: p.id,
          name: p.name,
          email: p.email ?? null,
          initials: makeInitials(p.name),
          color: colorForIndex(idx),
        })));
      })
      .catch((err) => {
        // Shown by the app's load error screen, with Retry.
        if (!cancelled) setLoadError(String(err?.message ?? err));
      })
      .finally(() => {
        if (!cancelled) setHydrated(true);
      });
    return () => { cancelled = true; };
  }, [loadKey]);

  const addPerson = useCallback((name: string, email?: string | null): Person => {
    const trimmed = name.trim();
    const normEmail = normalizeEmail(email);
    const cur = rosterRef.current;
    // Reuse existing: email is the stronger key (it's the identity Entra and
    // the directory give us, and what the API dedupes on); name is the
    // fallback for rows entered by hand before owners became directory-only.
    const { match: existing, backfillEmail } = resolvePersonReuse(cur, trimmed, normEmail);
    if (existing) {
      if (!backfillEmail || !normEmail) return existing;
      // A hand-entered row with no email, now matched to a directory person:
      // re-POST name + email so the server fills the email on the SAME row
      // (its create-or-reuse step 2). Optimistic locally, rolled back on error.
      const filled: Person = { ...existing, email: normEmail };
      setRoster(prev => prev.map(p => p.id === existing.id ? filled : p));
      const rollback = () => setRoster(prev => prev.map(p => p.id === existing.id ? { ...p, email: existing.email ?? null } : p));
      postPerson({ id: existing.id, name: existing.name, email: normEmail }, {
        localId: existing.id,
        rollback,
        failMsg: `Couldn't link "${existing.name}" to the directory`,
        // The server found another row already holding that email — the local
        // roster was stale. Our row stays as it was; the server's row is
        // brought in so the user can pick the right one.
        onOtherRow: (saved) => {
          rollback();
          setRoster(prev => upsertServerRow(prev, saved));
          toast.error(`"${saved.name}" is already on the roadmap — pick them again`);
        },
      });
      return filled;
    }

    const id = uuid();
    const idx = cur.length;
    const person: Person = {
      id,
      name: trimmed,
      email: normEmail,
      initials: makeInitials(trimmed),
      color: colorForIndex(idx),
    };

    // Optimistic insert.
    setRoster(prev => [...prev, person]);

    postPerson(normEmail ? { id, name: trimmed, email: normEmail } : { id, name: trimmed }, {
      localId: id,
      rollback: () => setRoster(prev => prev.filter(p => p.id !== id)),
      failMsg: `Couldn't save "${trimmed}"`,
      onOtherRow: (saved) => {
        if (isDifferentPerson(normEmail, saved)) {
          // The API reused a row by NAME that belongs to a different person
          // (two directory users share a display name). Refuse loudly rather
          // than bind this initiative — and its Slack nudges — to the wrong
          // human. Needs the API's step-2 rule tightened to allow the add.
          setRoster(prev => prev.filter(p => p.id !== id));
          toast.error(`A different "${saved.name}" is already on the roadmap`, {
            description: "Two people share this name. The roadmap API can't hold both yet — flag it to the team.",
          });
          return;
        }
        // Same person, already known to the server (stale local roster):
        // adopt the server's row in place of our optimistic one.
        setRoster(prev => prev.map(p => p.id === id
          ? { ...p, id: saved.id, name: saved.name, email: saved.email ?? p.email ?? null, initials: makeInitials(saved.name) }
          : p));
      },
    });

    return person;
  }, []);

  return { roster, hydrated, loadError, reload, addPerson };
}

type WirePerson = { id: string; name: string; email?: string | null };

// The one POST /people call site: fires the request, and on a response
// carrying a different id than we sent, hands the server's row to the caller
// (each path has its own reconcile rule). Failures roll back + toast.
function postPerson(
  payload: { id: string; name: string; email?: string },
  opts: { localId: string; rollback: () => void; failMsg: string; onOtherRow: (saved: WirePerson) => void }
) {
  api<WirePerson>("/people", { method: "POST", body: JSON.stringify(payload) })
    .then((saved) => {
      if (saved && saved.id !== opts.localId) opts.onOtherRow(saved);
    })
    .catch((err) => {
      opts.rollback();
      toast.error(opts.failMsg, { description: String(err?.message ?? err) });
    });
}

// Bring a server-known person into the roster (or refresh them if present).
function upsertServerRow(prev: Person[], saved: WirePerson): Person[] {
  const row: Person = {
    id: saved.id,
    name: saved.name,
    email: saved.email ?? null,
    initials: makeInitials(saved.name),
    color: colorForIndex(prev.length),
  };
  return prev.some(p => p.id === saved.id)
    ? prev.map(p => p.id === saved.id ? { ...p, ...row, color: p.color } : p)
    : [...prev, row];
}

export function useInitiatives() {
  // Holds ALL initiatives (incl. just-archived) so restore works; `active`
  // filters archived out for the returned list, same as Phase 1.
  const [all, setAll] = useState<Initiative[]>([]);
  const [hydrated, setHydrated] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadKey, setLoadKey] = useState(0);
  const reload = useCallback(() => {
    setHydrated(false);
    setLoadError(null);
    setLoadKey(k => k + 1);
  }, []);
  // Live snapshot so create() can compute the append order synchronously.
  const allRef = useRef<Initiative[]>([]);
  allRef.current = all;

  useEffect(() => {
    let cancelled = false;
    api<ApiInitiative[]>("/initiatives")
      .then((rows) => {
        if (cancelled) return;
        setAll(rows.map(fromApi));
      })
      .catch((err) => {
        // Shown by the app's load error screen, with Retry.
        if (!cancelled) setLoadError(String(err?.message ?? err));
      })
      .finally(() => {
        if (!cancelled) setHydrated(true);
      });
    return () => { cancelled = true; };
  }, [loadKey]);

  const create = useCallback((data: Omit<Initiative, "id" | "sortOrder">): Initiative => {
    const id = uuid();
    // New initiatives append to the end of their chosen quarter. Lifecycle
    // dates stamp when work starts (see baseline.ts) — only set here if born
    // directly into in-progress/shipped.
    const next: Initiative = {
      ...data,
      id,
      sortOrder: nextSortOrder(allRef.current, data.targetQ),
      ...lifecycleAtCreate(data),
    };

    setAll(prev => [next, ...prev]); // optimistic

    api<ApiInitiative>("/initiatives", {
      method: "POST",
      body: JSON.stringify(toApi(next)),
    })
      .then((saved) => {
        if (saved) setAll(prev => prev.map(i => i.id === id ? fromApi(saved) : i));
      })
      .catch((err) => {
        setAll(prev => prev.filter(i => i.id !== id)); // rollback
        toast.error(`Couldn't create "${next.name}"`, { description: String(err?.message ?? err) });
      });

    return next;
  }, []);

  const update = useCallback((id: string, patch: Partial<Initiative>) => {
    // First move into in-progress/shipped freezes the current target as the baseline.
    const existing = allRef.current.find(i => i.id === id);
    const stamped = existing ? withLifecycleStamps(existing, patch) : patch;
    let prevSnapshot: Initiative | undefined;
    setAll(prev => prev.map(i => {
      if (i.id !== id) return i;
      prevSnapshot = i;
      return { ...i, ...stamped };
    }));

    api<ApiInitiative>(`/initiatives/${id}`, {
      method: "PATCH",
      body: JSON.stringify(toApiPatch(stamped)),
    })
      .then((saved) => {
        if (saved) setAll(prev => prev.map(i => i.id === id ? fromApi(saved) : i));
      })
      .catch((err) => {
        if (prevSnapshot) {
          const snap = prevSnapshot;
          setAll(prev => prev.map(i => i.id === id ? snap : i)); // rollback
        }
        toast.error("Couldn't save changes", { description: String(err?.message ?? err) });
      });
  }, []);

  const archive = useCallback((id: string) => {
    const archivedAt = new Date().toISOString();
    let prevSnapshot: Initiative | undefined;
    setAll(prev => prev.map(i => {
      if (i.id !== id) return i;
      prevSnapshot = i;
      return { ...i, archivedAt };
    }));

    api(`/initiatives/${id}`, { method: "DELETE" })
      .catch((err) => {
        if (prevSnapshot) {
          const snap = prevSnapshot;
          setAll(prev => prev.map(i => i.id === id ? snap : i)); // rollback
        }
        toast.error("Couldn't archive", { description: String(err?.message ?? err) });
      });
  }, []);

  const restore = useCallback((id: string) => {
    let prevSnapshot: Initiative | undefined;
    setAll(prev => prev.map(i => {
      if (i.id !== id) return i;
      prevSnapshot = i;
      return { ...i, archivedAt: undefined };
    }));

    api(`/initiatives/${id}/restore`, { method: "POST" })
      .catch((err) => {
        if (prevSnapshot) {
          const snap = prevSnapshot;
          setAll(prev => prev.map(i => i.id === id ? snap : i)); // rollback
        }
        toast.error("Couldn't restore", { description: String(err?.message ?? err) });
      });
  }, []);

  const postUpdate = useCallback((initiativeId: string, input: { body: string; status: Status; authorId?: string | null; start?: DateTuple; shipped?: DateTuple }): Update => {
    const id = uuid();
    const update: Update = {
      id,
      initiativeId,
      authorId: input.authorId ?? null,
      body: input.body,
      status: input.status,
      createdAt: new Date().toISOString(),
    };

    // Optimistically reflect the new status on the initiative — posting an update
    // sets status; the API does the same server-side in one transaction.
    // Weekly updates are a status door too, so the lifecycle stamps apply here:
    // the composer passes the user-confirmed start/shipped dates for a
    // transition, and withLifecycleStamps fills any the user didn't provide
    // (first move into in-progress/shipped also freezes the current target).
    const existing = allRef.current.find(i => i.id === initiativeId);
    const patch: Partial<Initiative> = { status: input.status };
    if (input.start) patch.start = input.start;
    if (input.shipped) patch.shipped = input.shipped;
    const stamped = existing ? withLifecycleStamps(existing, patch) : patch;
    let prevSnapshot: Initiative | undefined;
    setAll(prev => prev.map(i => {
      if (i.id !== initiativeId) return i;
      prevSnapshot = i;
      return { ...i, ...stamped };
    }));

    const rollback = (err: unknown) => {
      if (prevSnapshot) {
        const snap = prevSnapshot;
        setAll(prev => prev.map(i => i.id === initiativeId ? snap : i));
      }
      toast.error("Couldn't post update", { description: String((err as Error)?.message ?? err) });
    };

    api(`/initiatives/${initiativeId}/updates`, {
      method: "POST",
      body: JSON.stringify({ id, authorId: update.authorId, body: input.body, status: input.status }),
    }).then(() => {
      // The updates endpoint carries the status but not the lifecycle dates —
      // persist those with a follow-up PATCH so a reload doesn't lose them.
      // (Two requests until the API takes the dates in the POST transaction.)
      const dates: Partial<Initiative> = {};
      // "in" checks, not truthiness — a reopen carries shipped: undefined,
      // which must persist as an explicit clear.
      if ("start" in stamped) dates.start = stamped.start;
      if ("shipped" in stamped) dates.shipped = stamped.shipped;
      if ("originalTarget" in stamped) dates.originalTarget = stamped.originalTarget;
      if (Object.keys(dates).length === 0) return;
      return api(`/initiatives/${initiativeId}`, {
        method: "PATCH",
        body: JSON.stringify(toApiPatch(dates)),
      }).then(() => undefined)
        .catch((err) => {
          // The POST already committed the status server-side — never roll
          // that back locally. Revert only the date stamps (they didn't
          // persist) and say exactly what failed.
          setAll(prev => prev.map(i => i.id === initiativeId
            ? { ...i, start: prevSnapshot?.start, shipped: prevSnapshot?.shipped, originalTarget: prevSnapshot?.originalTarget }
            : i));
          toast.error("Update posted, but the dates didn't save", {
            description: `Set the dates via Edit in the drawer. (${String((err as Error)?.message ?? err)})`,
          });
        });
    }).catch(rollback);

    return update;
  }, []);

  const active = all.filter(i => !i.archivedAt);

  return { initiatives: active, hydrated, loadError, reload, create, update, archive, restore, postUpdate };
}

// ---------- Updates (weekly progress) ----------

// Timeline of updates for one initiative (drawer). Quiet on failure so the UI
// shows an empty history if the endpoint isn't live yet.
export function useInitiativeUpdates(initiativeId: string | null | undefined) {
  const [updates, setUpdates] = useState<Update[]>([]);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (!initiativeId) { setUpdates([]); setHydrated(true); return; }
    let cancelled = false;
    setHydrated(false);
    api<Update[]>(`/initiatives/${initiativeId}/updates`)
      .then((rows) => { if (!cancelled) setUpdates(rows); })
      .catch(() => { if (!cancelled) setUpdates([]); })
      .finally(() => { if (!cancelled) setHydrated(true); });
    return () => { cancelled = true; };
  }, [initiativeId]);

  const prepend = useCallback((u: Update) => setUpdates(prev => [u, ...prev]), []);
  return { updates, hydrated, prepend };
}

// All updates since a timestamp, across initiatives (Weekly Review board).
export function useRecentUpdates(sinceISO?: string) {
  const [updates, setUpdates] = useState<Update[]>([]);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const q = sinceISO ? `?since=${encodeURIComponent(sinceISO)}` : "";
    api<Update[]>(`/updates${q}`)
      .then((rows) => { if (!cancelled) setUpdates(rows); })
      .catch(() => { if (!cancelled) setUpdates([]); })
      .finally(() => { if (!cancelled) setHydrated(true); });
    return () => { cancelled = true; };
  }, [sinceISO]);

  return { updates, hydrated };
}

// Every update across initiatives, newest first (Excel export). One request;
// the caller keeps only what it needs.
export function fetchAllUpdates(): Promise<Update[]> {
  return api<Update[]>("/updates");
}

// Keep the Status import referenced for downstream type consumers of this module.
export type { Status, Update };
